import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Seo from "../../components/Seo";
import { LoadingState, ErrorState } from "../../components/StateBlocks";
import useAuth from "../../hooks/useAuth";
import api, { ApiError } from "../../utils/api";

const STATUS_OPTIONS = ["DRAFT", "PLANNED", "CONFIRMED", "FULL", "CANCELED", "COMPLETED"];
const EVENT_AUDIENCE = "Grades 8-12 (ages 13-18)";

const EMPTY_FORM = {
  title: "",
  description: "",
  status: "DRAFT",
  startAt: "",
  endAt: "",
  capacity: 10,
  roomInfo: "",
  gradeRange: EVENT_AUDIENCE,
  organizerName: "",
  arrivalInstructions: "",
  accessibilityContact: "",
};

function toLocalInputValue(isoUtc) {
  if (!isoUtc) return "";
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toIsoUtc(localValue) {
  if (!localValue) return null;
  return new Date(localValue).toISOString();
}

export default function AdminEventForm({ mode }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { idToken } = useAuth();
  const [values, setValues] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mode !== "edit") return;
    api
      .adminGetEvent(idToken, eventId)
      .then((event) => {
        setValues({
          title: event.title,
          description: event.description,
          status: event.status,
          startAt: toLocalInputValue(event.startAt),
          endAt: toLocalInputValue(event.endAt),
          capacity: event.capacity,
          roomInfo: event.roomInfo || "",
          gradeRange: EVENT_AUDIENCE,
          organizerName: event.organizerName || "",
          arrivalInstructions: event.arrivalInstructions || "",
          accessibilityContact: event.accessibilityContact || "",
        });
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Unable to load event."))
      .finally(() => setLoading(false));
  }, [mode, eventId, idToken]);

  function updateField(field, value) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      ...values,
      capacity: Number(values.capacity),
      startAt: toIsoUtc(values.startAt),
      endAt: toIsoUtc(values.endAt),
    };
    try {
      if (mode === "create") {
        const created = await api.adminCreateEvent(idToken, payload);
        navigate(`/admin/events/${created.eventId}`);
      } else {
        await api.adminUpdateEvent(idToken, eventId, payload);
        navigate(`/admin/events/${eventId}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to save event.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingState label="Loading event\u2026" />;

  return (
    <>
      <Seo title={mode === "create" ? "New Event" : "Edit Event"} />
      <h1>{mode === "create" ? "Create a new event" : "Edit event"}</h1>
      {error && <ErrorState message={error} />}

      <form onSubmit={handleSubmit} className="card">
        <div className="form-field">
          <label htmlFor="ev-title">Title *</label>
          <input
            id="ev-title"
            type="text"
            required
            value={values.title}
            onChange={(e) => updateField("title", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ev-description">Description *</label>
          <textarea
            id="ev-description"
            required
            value={values.description}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ev-status">Status *</label>
          <select id="ev-status" value={values.status} onChange={(e) => updateField("status", e.target.value)}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="ev-startAt">Start (your local time) *</label>
          <input
            id="ev-startAt"
            type="datetime-local"
            required
            value={values.startAt}
            onChange={(e) => updateField("startAt", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ev-endAt">End (your local time) *</label>
          <input
            id="ev-endAt"
            type="datetime-local"
            required
            value={values.endAt}
            onChange={(e) => updateField("endAt", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ev-capacity">Capacity (max teens) *</label>
          <input
            id="ev-capacity"
            type="number"
            min="1"
            max="500"
            required
            value={values.capacity}
            onChange={(e) => updateField("capacity", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ev-roomInfo">Room info</label>
          <input
            id="ev-roomInfo"
            type="text"
            value={values.roomInfo}
            onChange={(e) => updateField("roomInfo", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ev-gradeRange">Audience</label>
          <select
            id="ev-gradeRange"
            value={values.gradeRange}
            onChange={(e) => updateField("gradeRange", e.target.value)}
          >
            <option value={EVENT_AUDIENCE}>{EVENT_AUDIENCE}</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="ev-organizerName">Organizer name</label>
          <input
            id="ev-organizerName"
            type="text"
            value={values.organizerName}
            onChange={(e) => updateField("organizerName", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ev-arrivalInstructions">Arrival instructions</label>
          <textarea
            id="ev-arrivalInstructions"
            value={values.arrivalInstructions}
            onChange={(e) => updateField("arrivalInstructions", e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ev-accessibilityContact">Accessibility contact</label>
          <input
            id="ev-accessibilityContact"
            type="text"
            value={values.accessibilityContact}
            onChange={(e) => updateField("accessibilityContact", e.target.value)}
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving\u2026" : "Save event"}
        </button>
      </form>
    </>
  );
}
